import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from './AuthContext';

const JobTrackerContext = createContext();

export const useJobTracker = () => useContext(JobTrackerContext);

export const JobTrackerProvider = ({ children }) => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const STATUSES = [
    { value: 'saved', label: 'Saved', color: 'secondary' },
    { value: 'applied', label: 'Applied', color: 'primary' },
    { value: 'interviewing', label: 'Interviewing', color: 'warning' },
    { value: 'offer', label: 'Offer', color: 'success' },
    { value: 'rejected', label: 'Rejected', color: 'danger' },
  ];

  useEffect(() => {
    if (user) fetchApplications();
  }, [user]);

  const fetchApplications = async () => {
    const { data } = await supabase
      .from('job_applications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setApplications(data || []);
    setLoading(false);
  };

  const addApplication = async (application) => {
    const { data, error } = await supabase
      .from('job_applications')
      .insert([{ ...application, user_id: user.id }])
      .select();
    if (!error) {
      setApplications([data[0], ...applications]);
      return data[0];
    }
    throw error;
  };

  const updateStatus = async (id, status) => {
    const { error } = await supabase
      .from('job_applications')
      .update({ status })
      .eq('id', id);
    if (!error) {
      setApplications(applications.map(a => a.id === id ? { ...a, status } : a));
    }
  };

  const deleteApplication = async (id) => {
    const { error } = await supabase
      .from('job_applications')
      .delete()
      .eq('id', id);
    if (!error) {
      setApplications(applications.filter(a => a.id !== id));
    }
  };

  const getStatusColor = (status) => {
    return STATUSES.find(s => s.value === status)?.color || 'secondary';
  };

  return (
    <JobTrackerContext.Provider value={{
      applications,
      loading,
      STATUSES,
      addApplication,
      updateStatus,
      deleteApplication,
      getStatusColor,
      fetchApplications
    }}>
      {children}
    </JobTrackerContext.Provider>
  );
};